import { Loader2, Send, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { fmtDateTime } from '../../../shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useActor } from '../../api'
import { useStaffLookup } from '../../ui'
import { useTaskActions } from '../../taskApi'
import { COMMENT_VISIBILITIES, type CommentVisibility, type Project, type Task } from '../../types'
import { toastError } from '../../ui'
import { FileList } from '../FileList'
import { useHistoryText } from '../../history'

/** Etiket metni: boşluksuz ad (vurgulama ve metinde arama bu biçime dayanır). */
const mentionToken = (name: string) => '@' + name.trim().replace(/\s+/g, '_')

/** Mention'ları (@ad) vurgular; HTML üretmez. */
function CommentBody({ body }: { body: string }) {
  return (
    <p className="whitespace-pre-wrap break-words text-sm">
      {body.split(/(@[\p{L}\d_]+)/u).map((part, i) =>
        part.startsWith('@') ? (
          <span key={i} className="font-medium text-primary">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </p>
  )
}

export function TaskActivity({ project, task, canWrite }: { project: Project; task: Task; canWrite: boolean }) {
  const { t } = useTranslation()
  const historyText = useHistoryText()
  const actor = useActor()
  const { user } = useAuth()
  const { nameOf, staff } = useStaffLookup()
  const { addComment, deleteComment } = useTaskActions(project.id, task.id)
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<CommentVisibility>('internal')
  // Etiketlenen kişiler: metne @Ad eklenir, gönderimde metinde hâlâ geçenler bildirim alır.
  const [mentioned, setMentioned] = useState<Record<string, string>>({})
  const mention = (id: string) => {
    const u = staff.find((x) => x.id === id)
    if (!u) return
    const token = mentionToken(u.name)
    setMentioned((m) => ({ ...m, [id]: token }))
    setBody((b) => (b.includes(token) ? b : `${b}${b && !b.endsWith(' ') ? ' ' : ''}${token} `))
  }
  const files = project.files.filter((f) => f.taskId === task.id)

  const send = (e: React.FormEvent) => {
    e.preventDefault()
    if (!body.trim()) return
    const mentions = Object.entries(mentioned).filter(([, token]) => body.includes(token)).map(([id]) => id)
    addComment.mutate(
      { body, visibility, mentions },
      {
        onSuccess: () => {
          setBody('')
          setMentioned({})
        },
        onError: toastError,
      },
    )
  }
  const remove = async (id: string) => {
    if (!(await confirmModal({ title: t('projects.taskDetail.deleteCommentTitle'), description: t('projects.taskDetail.deleteCommentBody'), confirmLabel: t('common.delete'), destructive: true }))) return
    deleteComment.mutate(id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }

  return (
    <section className="rounded-xl border bg-card shadow-card">
      <Tabs defaultValue="comments">
        <div className="border-b px-4 py-3">
          <TabsList size="sm">
            <TabsTrigger value="comments">{t('projects.taskDetail.commentsCount', { count: task.comments.length })}</TabsTrigger>
            <TabsTrigger value="history">{t('projects.taskDetail.history')}</TabsTrigger>
            <TabsTrigger value="files">{t('projects.detail.tabs.files')}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="comments" className="space-y-4 p-4">
          {task.comments.length === 0 ? <p className="text-sm text-muted-foreground">{t('projects.taskDetail.noComments')}</p> : null}
          <ul className="space-y-4">
            {task.comments.map((c) => (
              <li key={c.id} className="flex gap-3">
                <Avatar name={nameOf(c.author)} className="size-9" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{nameOf(c.author)}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{fmtDateTime(c.at)}</span>
                    <Badge tone={c.visibility === 'shared' ? 'success' : 'info'} className="ml-auto rounded-md">
                      {t(`projects.visibility.${c.visibility}`)}
                    </Badge>
                    {canWrite && c.author === user?.id ? (
                      <Button size="icon" variant="ghost" className="size-7 hover:text-destructive" aria-label={t('projects.taskDetail.deleteCommentTitle')} onClick={() => void remove(c.id)}>
                        <Trash2 />
                      </Button>
                    ) : null}
                  </div>
                  <CommentBody body={c.body} />
                </div>
              </li>
            ))}
          </ul>
          {canWrite ? (
            <form onSubmit={send} className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-end">
              <Avatar name={actor} className="hidden size-9 sm:flex" />
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder={t('projects.taskDetail.commentPlaceholder')}
                aria-label={t('projects.taskDetail.commentPlaceholder')}
                className="min-w-0 flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(e)
                }}
              />
              <div className="flex gap-2">
                <Select
                  size="default"
                  searchable
                  className="min-w-0 flex-1 sm:w-44 sm:flex-none"
                  value=""
                  onChange={mention}
                  placeholder={t('projects.taskDetail.mention')}
                  aria-label={t('projects.taskDetail.mention')}
                  options={staff.filter((u) => u.id !== user?.id).map((u) => ({ value: u.id, label: u.name }))}
                />
                <Select size="default" className="min-w-0 flex-1 sm:w-40 sm:flex-none" value={visibility} onChange={setVisibility} aria-label={t('projects.taskDetail.visibility')} options={COMMENT_VISIBILITIES.map((v) => ({ value: v, label: t(`projects.visibility.${v}`) }))} />
                <Button type="submit" disabled={!body.trim() || addComment.isPending}>
                  {addComment.isPending ? <Loader2 className="animate-spin" /> : <Send />}
                  {t('projects.taskDetail.send')}
                </Button>
              </div>
            </form>
          ) : null}
        </TabsContent>
        <TabsContent value="history" className="p-4">
          <ol className="space-y-3">
            {task.history.map((h) => (
              <li key={h.id} className="flex flex-wrap gap-x-2 text-sm">
                <span className="w-full shrink-0 text-xs tabular-nums text-muted-foreground sm:w-36">{fmtDateTime(h.at)}</span>
                <span className="font-medium">{nameOf(h.by)}</span>
                <span>{historyText(h)}</span>
              </li>
            ))}
          </ol>
        </TabsContent>
        <TabsContent value="files" className="p-4">
          <FileList project={project} files={files} canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </section>
  )
}
